// Error tracking utility for CloudCache app
import config, { initWorkerBase, getWorkerBase } from '../config';

class ErrorTracker {
  constructor() {
    this.errors = [];
    this.maxErrors = 100; // Keep last 100 errors
    this.errorCount = 0;
  }

  // Track an error with context
  trackError(error, context = {}) {
    const errorInfo = {
      id: this.generateErrorId(),
      timestamp: new Date().toISOString(),
      error: error.message || error.toString(),
      stack: error.stack,
      context: {
        page: context.page || 'unknown',
        action: context.action || 'unknown',
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...context
      },
      severity: this.calculateSeverity(error, context)
    };

    this.errors.unshift(errorInfo);
    this.errorCount++;

    // Keep only the last maxErrors
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    // Send to admin dashboard in real-time
    this.sendToAdminDashboard(errorInfo);

    // Log to console for development only
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('Error tracked:', errorInfo);
    }

    return errorInfo.id;
  }

  // Generate unique error ID
  generateErrorId() {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Calculate error severity
  calculateSeverity(error, context) {
    if (context.page === 'registration' || context.action === 'onboarding') {
      return 'high'; // Critical for customer onboarding
    }
    if (context.page === 'pricing' || context.action === 'billing') {
      return 'medium'; // Important for business
    }
    return 'low'; // General errors
  }

  // Send error to admin dashboard
  async sendToAdminDashboard(errorInfo) {
    try {
      await initWorkerBase();
      const base = getWorkerBase();
      if (!base) return; // no worker configured yet
      await fetch(`${base}/api/admin/error-track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorInfo)
      });
    } catch (sendError) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('Failed to send error to admin dashboard:', sendError);
      }
    }
  }

  // Get all errors
  getErrors() {
    return this.errors;
  }

  // Get error count
  getErrorCount() {
    return this.errorCount;
  }

  // Get errors by severity
  getErrorsBySeverity(severity) {
    return this.errors.filter(err => err.severity === severity);
  }

  // Get errors by page
  getErrorsByPage(page) {
    return this.errors.filter(err => err.context.page === page);
  }

  // Clear errors
  clearErrors() {
    this.errors = [];
    this.errorCount = 0;
  }

  // Get error summary for admin dashboard
  getErrorSummary() {
    const summary = {
      total: this.errorCount,
      bySeverity: {
        high: this.getErrorsBySeverity('high').length,
        medium: this.getErrorsBySeverity('medium').length,
        low: this.getErrorsBySeverity('low').length
      },
      byPage: {},
      recentErrors: this.errors.slice(0, 10)
    };

    // Count errors by page
    this.errors.forEach(err => {
      const page = err.context.page;
      summary.byPage[page] = (summary.byPage[page] || 0) + 1;
    });

    return summary;
  }
}

// Create singleton instance
const errorTracker = new ErrorTracker();

// Global error handler
window.addEventListener('error', (event) => {
  errorTracker.trackError(event.error, {
    page: window.location.pathname,
    action: 'unhandled_error'
  });
});

// Promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  errorTracker.trackError(event.reason, {
    page: window.location.pathname,
    action: 'unhandled_promise_rejection'
  });
});

export default errorTracker;
